<template>
  <section class="multiple-choice-question-container">
    <div
      v-if="!patientBoxHtml && patientBoxTopicId"
      class="aside-container"
    >
      <loading-bar></loading-bar>
    </div>
    <div
      v-else-if="patientBoxHtml"
      data-test="patient-box"
      class="aside-container"
      v-html="patientBoxHtml"
    >
    </div>
    <div class="question-container">
      <div
        v-if="isSimulationComplete"
        class="flashcard-complete"
      >
        <span
          :class="{
            'correct-icon': flashcardState.correct,
            'incorrect-icon': !flashcardState.correct,
          }"
        ></span>
        {{ correctText }}
      </div>
      <div
        class="flashcard-content"
        v-html="questionHtml"
      >
      </div>
      <div
        class="flashcard-answers"
      >
        <multiple-choice-answer
          v-for="answer in answers"
          :key="answer.id"
          :answer="answer"
          :selected="selectedAnswerId === answer.id"
          :is-simulation-complete="isSimulationComplete"
          @select-answer="selectAnswer"
          @toggle-strike-through="toggleStrikeThrough"
        >
        </multiple-choice-answer>
      </div>
    </div>
  </section>
</template>

<script>
import MultipleChoiceAnswer from '@/components/sims/flashcards/MultipleChoiceAnswer.vue';
import MultipleChoiceMixin from '@/mixins/multipleChoice.js';
import LoadingBar from '@/components/singletons/loading-bar';
import MathMlMixin from '@/mixins/mathml';

export default {
  components: {
    MultipleChoiceAnswer,
    LoadingBar,
  },
  mixins: [MultipleChoiceMixin, MathMlMixin],
  props: {
    flashcard: {
      type: Object,
      required: true,
    },
    selectedAnswerId: {
      type: Number,
      required: true,
    },
    patientBoxTopicId: {
      type: Number,
      required: false,
    },
    patientBoxHtml: {
      type: String,
      required: false,
    },
    questionHtml: {
      type: String,
      required: true,
    },
    isSimulationComplete: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['selectAnswer', 'toggleStrikeThrough'],
  computed: {
    flashcardState() {
      return this.$attrs['flashcard-state'] || {};
    },
    correctText() {
      if (!this.selectedAnswerId) return 'Incorrect - Unanswered';
      return this.flashcardState.correct ? 'Correct' : 'Incorrect';
    },
  },
  methods: {
    selectAnswer(answerId) {
      if (this.selectedAnswerId === answerId) {
        this.$emit('selectAnswer', null);
      } else {
        this.$emit('selectAnswer', answerId);
      }
    },
    stripStyle(html) {
      if (!html) {
        return '';
      }
      return html.replaceAll(/style="[^"]*"/g, '');
    },
    toggleStrikeThrough(answer, strikeThroughValue) {
      this.$emit('toggleStrikeThrough', answer, strikeThroughValue);
    },
  },
};
</script>

<style lang='scss' scoped>
.multiple-choice-question-container {
  margin-top: 2.5rem;
  margin-bottom: 2.5rem;
  flex-grow: 1;
  display: flex;
  justify-content: center;
  gap: 3.5rem;
  font-size: 1.5rem;

  .aside-container {
    min-width: 350px;
  }

  .flashcard-answers {
    margin-bottom: 1rem;
  }

  .question-container {
    max-width: 848px;
  }
}

.correct-icon {
  display: inline-block;
  background-color: #00B46D;
  box-shadow: 0 0 0 3px #00B46D, 0 0 0 4px #00B46D;
  background-image: url(/src/assets/icons/check-ffffff.svg);
  border-radius: 50%;
  width: 18px;
  height: 18px;
  margin-right: 1rem;
}

.incorrect-icon {
  display: inline-block;
  background-color: #DC3416;
  box-shadow: 0 0 0 3px #DC3416, 0 0 0 4px #DC3416;
  background-image: url(/src/assets/icons/cross-ffffff.svg);
  border-radius: 50%;
  width: 18px;
  height: 18px;
  margin-right: 1rem;
}

.flashcard-complete {
  margin-bottom: 1rem;
  font-size: 1.5rem;
  font-weight: 600;
}

// media for screen sizes up to 1440px
@media (max-width: 1279px) {
  .multiple-choice-question-container {
    flex-direction: column;
    gap: 2rem;
    align-items: center;
  }
}
</style>

<style lang='scss'>
.multiple-choice-question-container{

  .flashcard-content>*:first-child {
    margin-top: 0 !important;
    line-height: 2.25rem;
    font-weight: 600;
  }
  .aside-container {
    table, th, td {
      border: 2px solid #000000;
      border-collapse: collapse;
    }

    tr:nth-child(odd){
      background-color: rgba(216, 224, 243, 1);
      font-weight: 700;
      font-size: 18px;
    }

    tr:nth-child(even){
      background-color: white;
      font-weight: 400;
      font-size: 16px;

      td {
        padding-top: 1.5rem;
        padding-bottom: 1.5rem;
      }
    }

    th, td {
      padding: 1rem;

      *:first-child {
        margin-top: 0;
      }

      *:last-child {
        margin-bottom: 0;
      }
    }
  }
}
</style>
